<script setup lang="ts">
import IconCount from "@/components/Objects/IconCount.vue";
import {useShipDesignStore} from "@/stores/shipDesignStore.ts";
import ModalTrigger from '@/components/Interface/Modal/ModalTrigger.vue'
import ShipDesignInUse from '@/components/Sections/Command/ShipDesigns/ShipDesignInUse.vue'
import ShipDesignInProduction from '@/components/Sections/Command/ShipDesigns/ShipDesignInProduction.vue'
import Backend from '@/models/backend.ts'
import CellFixedWidth from '@/components/Interface/DataTable/Cell/CellFixedWidth.vue'
import HeaderFixedWidthIcon from '@/components/Interface/DataTable/Headers/HeaderFixedWidthIcon.vue'
import CellFlexWidth from '@/components/Interface/DataTable/Cell/CellFlexWidth.vue'
import ActionBarSearch from '@/components/Interface/DataTable/ActionBar/ActionBarSearch.vue'
import FerionDataTable from '@/components/Interface/DataTable/FerionDataTable.vue'
import ActionBarFiller from '@/components/Interface/DataTable/ActionBar/ActionBarFiller.vue'
import HeaderTextFlex from '@/components/Interface/DataTable/Headers/HeaderTextFlex.vue'
import ActionBarBlock from '@/components/Interface/DataTable/ActionBar/ActionBarBlock.vue'
import { useTableStore } from '@/stores/tableStore.ts'
import HeaderFixedWidthTextNoSort from '@/components/Interface/DataTable/Headers/HeaderFixedWidthTextNoSort.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import HeaderFixedWidthText from '@/components/Interface/DataTable/Headers/HeaderFixedWidthText.vue'
import { useShipDesignCreationStore } from '@/stores/shipDesignCreationStore.ts'
import type PlayerShipDesign from '@/types/PlayerShipDesign.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'

const shipDesignStore = useShipDesignStore();
const tableStore = useTableStore();
const gameStore = useGameStore()
const shipDesignCreationStore = useShipDesignCreationStore()

const deleteShipDesign = (id: number) => {
  const backend = new Backend();
  backend.deleteShipDesign(id);
}

const filters = (shipDesigns:Map<number,PlayerShipDesign>) => {
  return Array.from(shipDesigns.values())
    .filter((shipDesign) => {
      return !shipDesign.deleted
    })
    .filter((shipDesign) => {
      // If no search query, return all ships
      if (!tableStore.shipDesigns.filters.nameSearch || tableStore.shipDesigns.filters.nameSearch === '') {
        return true;
      }
      // Otherwise, filter based on the ship name (case-insensitive search)
      return shipDesign.name.toLowerCase().includes(tableStore.shipDesigns.filters.nameSearch.toLowerCase());
    })
};

const cloneDesign = (id: number) => {
  gameStore.modalType = 'createShipDesign'
  const design = shipDesignStore.playerShipDesigns.get(id)
  if(!design) {
    return
  }
  shipDesignCreationStore.initFromDesign(design)
}

const editShipDesignName = (id: number) => {
  gameStore.modalType = 'editShipDesignName'
  gameStore.modalData = { shipDesignId: id }
}


</script>

<template>
  <div class="p-2">
  <FerionDataTable
    :data="shipDesignStore.playerShipDesigns"
    tableStoreName="shipDesigns"
    :selector="false"
    :filters="filters"
    :heightLimit="'h-[calc(100vh-9.5rem)]'"
  >
    <!--- ActionBar --->
    <template #actionBar>
      <ActionBarBlock class="mr-1 pl-2 flex items-center rounded">
        <ModalTrigger type="createShipDesign">
          <ActionButton class="my-1">Create new design</ActionButton>
        </ModalTrigger>
      </ActionBarBlock>
      <ActionBarFiller class="mr-1 rounded"></ActionBarFiller>
      <ActionBarSearch v-model="tableStore.shipDesigns.filters.nameSearch" name="Name" placeHolder="Search..." class=""/>
    </template>
    <!--- Headers --->
    <template #columns="{ sort, sortColumn, sortDirection }">
      <HeaderTextFlex min-width="12" column="name" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort" class="rounded-tl">
        <div class="pl-2">Name</div>
      </HeaderTextFlex>
      <HeaderFixedWidthText width="8" align="left" column="type" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
        Type
      </HeaderFixedWidthText>
      <HeaderFixedWidthIcon width="5" column="hitPoints" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
        <GenericIcon tooltip="Hit points" type="hit_points"></GenericIcon>
      </HeaderFixedWidthIcon>
      <HeaderFixedWidthIcon width="5" column="productionCost" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
        <GenericIcon tooltip="Production cost" type="production"></GenericIcon>
      </HeaderFixedWidthIcon>
      <HeaderFixedWidthIcon width="5" column="attackShips" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
        <GenericIcon tooltip="Attack strength against ships" type="attack_against_ships"></GenericIcon>
      </HeaderFixedWidthIcon>
      <HeaderFixedWidthIcon width="5" column="attackPlanets" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
        <GenericIcon tooltip="Attack strength against planet" type="attack_against_planets"></GenericIcon>
      </HeaderFixedWidthIcon>
      <HeaderFixedWidthIcon width="5" column="shields" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
        <GenericIcon tooltip="Shields" type="shields"></GenericIcon>
      </HeaderFixedWidthIcon>
      <HeaderFixedWidthIcon width="5" column="maxSpeed" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
        <GenericIcon tooltip="Engine speed" type="max_speed"></GenericIcon>
      </HeaderFixedWidthIcon>
      <HeaderFixedWidthIcon width="5" column="scanningRange" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
        <GenericIcon tooltip="Scanning range" type="scanning_range"></GenericIcon>
      </HeaderFixedWidthIcon>
      <HeaderFixedWidthIcon width="5" column="repair" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
        <GenericIcon tooltip="Repair capacity" type="repair"></GenericIcon>
      </HeaderFixedWidthIcon>
      <HeaderFixedWidthIcon width="5" column="maintenanceCost" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
        <GenericIcon type="credit"></GenericIcon>
      </HeaderFixedWidthIcon>
      <HeaderFixedWidthTextNoSort width="5" align="center">
        Special
      </HeaderFixedWidthTextNoSort>
      <HeaderFixedWidthTextNoSort width="8" align="center">
        In production
      </HeaderFixedWidthTextNoSort>
      <HeaderFixedWidthTextNoSort width="5" align="center">
        In use
      </HeaderFixedWidthTextNoSort>
      <HeaderFixedWidthTextNoSort width="7" align="center" class="rounded-tr">
        Action
      </HeaderFixedWidthTextNoSort>
    </template>
    <!--- Rows --->
    <template #rows="{ item }">
      <CellFlexWidth min-width="12" >
        <div class="flex-col pl-2">
          <div>{{ item.name }}</div>
        </div>
      </CellFlexWidth>
      <CellFixedWidth width="8" align="left">
        {{ item.typePretty }}
      </CellFixedWidth>
      <CellFixedWidth width="5">
        <IconCount :amount="item.hitPoints" :no-plus="true"/>
      </CellFixedWidth>
      <CellFixedWidth width="5">
        <IconCount :amount="item.productionCost" :no-plus="true"/>
      </CellFixedWidth>
      <CellFixedWidth width="5">
        <IconCount :amount="item.attackShips" :no-plus="true"/>
      </CellFixedWidth>
      <CellFixedWidth width="5">
        <IconCount :amount="item.attackPlanets" :no-plus="true"/>
      </CellFixedWidth>
      <CellFixedWidth width="5">
        <IconCount :amount="item.shields" :no-plus="true"/>
      </CellFixedWidth>
      <CellFixedWidth width="5">
        <IconCount :amount="item.maxSpeed" :no-plus="true"/>
      </CellFixedWidth>
      <CellFixedWidth width="5">
        <IconCount :amount="item.scanningRange" :no-plus="true"/>
      </CellFixedWidth>
      <CellFixedWidth width="5">
        <IconCount :amount="item.repair" :no-plus="true"/>
      </CellFixedWidth>
      <CellFixedWidth width="5">
        <IconCount :amount="-item.maintenanceCost" :no-plus="true"/>
      </CellFixedWidth>
      <CellFixedWidth width="5">
        <GenericIcon  v-if="item.canSettle" type="cmd_settle"/>
      </CellFixedWidth>
      <CellFixedWidth width="8">
        <ShipDesignInProduction :shipDesignId="item.id"></ShipDesignInProduction>
      </CellFixedWidth>
      <CellFixedWidth width="5">
        <ShipDesignInUse :shipDesignId="item.id"></ShipDesignInUse>
      </CellFixedWidth>
      <CellFixedWidth width="7">
        <div class="flex">
          <ActionButton @click="cloneDesign(item.id)" :for-icon="true" class="mr-2" title="Clone design"><GenericIcon type="copy"/></ActionButton>
          <ActionButton @click="editShipDesignName(item.id)" :for-icon="true" class="mr-2" title="Edit name"><GenericIcon type="edit"/></ActionButton>
          <ActionButton @click.once="deleteShipDesign(item.id)" :for-icon="true" title="Delete design"><GenericIcon type="trash"/></ActionButton>
        </div>
      </CellFixedWidth>
    </template>
  </FerionDataTable>
  </div>
</template>

<style scoped lang="scss">

</style>